/**
 * /////////////////////////////////////////////////////////////////////////////
 *
 * catalogManagerInstance.ts is the primary interface between sat-cruncher and the main application.
 * It manages all interaction with the satellite catalogue.
 * https://keeptrack.space
 *
 * @Copyright (C) 2025 Kruczek Labs LLC
 * @Copyright (C) 2015-2016, James Yoder
 *
 * Original source code released by James Yoder at https://github.com/jeyoder/ThingsInSpace/
 * under the MIT License. Please reference https://keeptrack.space/license/thingsinspace.txt
 *
 * KeepTrack is free software: you can redistribute it and/or modify it under the
 * terms of the GNU Affero General Public License as published by the Free Software
 * Foundation, either version 3 of the License, or (at your option) any later version.
 *
 * KeepTrack is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License along with
 * KeepTrack. If not, see <http://www.gnu.org/licenses/>.
 *
 * /////////////////////////////////////////////////////////////////////////////
 */

import { controlSites } from '@app/app/data/catalogs/control-sites';
import { launchSiteObjects, launchSites } from '@app/app/data/catalogs/launch-sites';
import { sensors } from '@app/app/data/catalogs/sensors';
import { stars } from '@app/app/data/catalogs/stars';
import { GetSatType, MissileParams } from '@app/engine/core/interfaces';
import { ServiceLocator } from '@app/engine/core/service-locator';
import { errorManagerInstance } from '@app/engine/utils/errorManager';
import { isThisNode } from '@app/engine/utils/isThisNode';
import { KeepTrack } from '@app/keeptrack';
import {
  BaseObject,
  CatalogSource,
  Degrees,
  Kilometers,
  KilometersPerSecond,
  Radians,
  Satellite,
  SatelliteRecord,
  Sgp4,
  SpaceObjectType,
  Star,
  TemeVec3,
  Tle,
  TleLine1,
  TleLine2,
} from '@ootk/src/main';
import { SatMath } from '../analysis/sat-math';
import { SatCruncherThreadManager } from '../threads/sat-cruncher-thread-manager';
import { SplashScreen } from '../ui/splash-screen';
import { StringExtractor } from '../ui/string-extractor';
import { LaunchSite } from './catalog-manager/LaunchFacility';
import { MissileObject } from './catalog-manager/MissileObject';
import { SatLinkManager } from './catalog-manager/satLinkManager';

declare module '@app/engine/core/interfaces' {
  interface SatCruncherMessageData {
    extraData?: string;
    extraUpdate?: boolean;
    /**
     * Object id that is now being skipped by the cruncher
     * due to a bad TLE. VIMPEL don't have satids so we use the
     * object id instead.
     */
    badObjectId?: number;
    // JSON string
    satId?: number;
    sensorMarkerArray?: number[];
    /** Catalog sequence number for discarding stale messages after catalog swap */
    seqNum?: number;
  }
  interface UserSettings {
    installDirectory: string;
  }
}

export interface DensityBin {
  minAltitude: number;
  maxAltitude: number;
  count: number;
  density: number; // spatial density (objects per km³)
}

/**
 * Entry in {@link CatalogManager.staticSet}. The set is a heterogeneous bag of
 * Star / DetailedSensor / LaunchSite instances plus control-site plain objects
 * (and plugin-injected entries), so only the properties the app reads off the
 * shared collection are typed here; consumers narrow or cast per entry kind.
 */
export interface StaticSetEntry {
  id: number;
  name: string;
  type: SpaceObjectType;
  /** Present (and truthy) only on sensor entries. */
  maxRng?: Kilometers;
  // Communication link flags carried by control-site entries.
  linkAehf?: boolean;
  linkWgs?: boolean;
  linkIridium?: boolean;
  linkGalileo?: boolean;
  linkStarlink?: boolean;
}

export class CatalogManager {
  private static readonly TEMPLATE_INTLDES = '58001A';
  private static readonly TEMPLATE_TLE1_BEGINNING = '1 ';
  private static readonly TEMPLATE_TLE1_ENDING = 'U 58002B   17115.48668720 +.00000144 +00000-0 +16234-3 0  9994';
  private static readonly TEMPLATE_TLE2_BEGINNING = '2 ';
  private static readonly TEMPLATE_TLE2_ENDING = ' 034.2502 167.2636 0042608 222.6554 121.5501 14.84703551080477';
  static readonly ANALYST_START_ID = 90000;

  analSatSet = <Satellite[]>[];
  cosparIndex: { [key: string]: number } = {};
  fieldOfViewSet = [] as {
    static: boolean;
    marker: boolean;
    id: number;
  }[];
  isLaunchSiteManagerLoaded = false;
  isSensorManagerLoaded = false;
  isStarManagerLoaded = false;
  launchSites: {
    [key: string]: {
      name: string;
      lat: Degrees;
      lon: Degrees;
    };
  } = {};

  missileSats: number = 0;
  missileSet = [] as MissileObject[];
  /**
   * Absolute objectCache ids of the currently-occupied OEM satellite slots. Maintained
   * by {@link OemSlotAllocator} (add on allocate, remove on free) so the per-frame
   * position interpolation can iterate only the handful of live OEM satellites instead
   * of scanning all `settingsManager.maxOemSatellites` reserved placeholder slots.
   */
  oemSatelliteIds = new Set<number>();
  numSatellites: number = 0;
  numObjects: number = 0;
  orbitDensity: DensityBin[] = [];
  orbitDensityMax = 0;
  orbitalPlaneDensity: number[][] = [];
  orbitalPlaneDensityMax = 0;
  orbitalSats: number;
  satCruncherThread: SatCruncherThreadManager;
  /** @deprecated Use satCruncherThread instead */
  get satCruncher(): Worker {
    return this.satCruncherThread?.worker as Worker;
  }

  objectCache: BaseObject[];
  satExtraData;
  satLinkManager: SatLinkManager;
  sccIndex: { [key: string]: number } = {};
  sensorMarkerArray: number[] = [];
  starIndex1 = 0;
  starIndex2 = 0;
  staticSet: StaticSetEntry[] = [];

  /**
   * Calculates the Satellite Record (satrec) for a given satellite object.
   * If a cached satrec exists, it returns it. Otherwise, it performs and stores
   * satellite initialization calculations using the Sgp4.createSatrec method.
   * The calculated satrec is then cached for later use.
   *
   * @param {SatObject} sat - The satellite object for which to calculate the satrec.
   * @returns {SatelliteRecord} The calculated or cached Satellite Record.
   */
  calcSatrec(sat: Satellite): SatelliteRecord {
    // If cached satrec exists, return it
    if (sat.satrec) {
      return sat.satrec;
    }

    // Perform and store sat init calcs
    const satrec = Sgp4.createSatrec(sat.tle1, sat.tle2);

    // Cache the satrec for later use.
    if (this.objectCache[sat.id]?.isSatellite()) {
      (this.objectCache[sat.id] as Satellite).satrec = satrec;
    } else {
      errorManagerInstance.warn('calcSatrec: satId not found in satData');
    }

    return satrec;
  }

  /**
   * Converts an array of satellite numbers to an array of corresponding IDs.
   *
   * @param {number[]} satnumArray - An array of satellite numbers.
   * @returns {number[]} - An array of IDs corresponding to the satellite numbers.
   * If a satellite number does not have a corresponding ID, it is not included in the returned array.
   */
  satnums2ids(satnumArray: number[]): number[] {
    // sccNum2Id normalizes numeric input itself (strips leading zeros) so the
    // padStart that used to live here is no longer needed.
    return satnumArray.map((satnum) => this.sccNum2Id(satnum, false)).filter((id): id is number => id !== null);
  }

  /**
   * Converts an international designation to its corresponding ID.
   * @param intlDes The international designation to convert.
   * @returns The corresponding ID if found, otherwise null.
   */
  intlDes2id(intlDes: string): number | null {
    return typeof this.cosparIndex[`${intlDes}`] !== 'undefined' ? this.cosparIndex[`${intlDes}`] : null;
  }

  /**
   * This method is used to get the ID from the object number.
   *
   * @param {number} a5Num - The NORAD satellite catalog number.
   * @param {boolean} isExtensiveSearch - A flag to determine if an extensive search should be performed. Default is true.
   *
   * @returns {number | null} - Returns the ID if found, otherwise returns null.
   *
   * The method first checks if the object number exists in the `sccIndex`. If it does, it returns the corresponding ID.
   * If the object number does not exist in the `sccIndex` and `isExtensiveSearch` is true, it performs an extensive search in the `satData`.
   * If the object number is found in the `satData`, it returns the index as the ID. If not found, it returns null.
   */
  sccNum2Id(a5Num: string | number, isExtensiveSearch = true): number | null {
    // Normalize input to the display-canonical natural-number form so user
    // input of "5", "00005", or 5 (all referring to Vanguard) collapses to
    // a single sccIndex key. Alpha-5 ("T0001") and extended (7+ digit) IDs
    // have no leading zeros and pass through unchanged.
    let key = typeof a5Num === 'number' ? a5Num.toString() : a5Num;

    if (/^0+\d/u.test(key)) {
      key = key.replace(/^0+/u, '');
    }

    // Fast path: direct sccIndex hit.
    if (typeof this.sccIndex[key] !== 'undefined') {
      return this.sccIndex[key];
    }

    // Equivalence path: the catalog loader indexes alpha-5 inputs under their
    // 6-digit numeric form (Tle.convertA5to6Digit), so a user typing "T0001"
    // would otherwise miss a satellite stored at sccIndex["270001"]. Try the
    // 6↔A5 conversion before falling through to extensive search.
    const kind = Tle.classifySatNum(key);

    if (kind === 'alpha5' || kind === 'numeric6') {
      try {
        const altKey = kind === 'alpha5' ? Tle.convertA5to6Digit(key) : Tle.convert6DigitToA5(key);

        if (typeof this.sccIndex[altKey] !== 'undefined') {
          return this.sccIndex[altKey];
        }
      } catch {
        // Conversion threw — fall through to extensive search.
      }
    }

    if (isExtensiveSearch) {
      for (let i = 0; i < this.objectCache.length; i++) {
        const obj = this.objectCache[i];

        if (!(obj instanceof Satellite)) {
          continue;
        }
        // Match the canonical sccNum and both derived forms so user input in
        // any of the three forms (numeric, alpha-5, 6-digit) resolves.
        if (obj.sccNum === key || obj.sccNum5 === key || obj.sccNum6 === key) {
          return i;
        }
      }
    }

    return null;
  }

  /**
   * Retrieves a satellite object based on its object number.
   *
   * @param sccNum - The object number of the satellite.
   * @returns The satellite object if found, null otherwise.
   */
  sccNum2Sat(sccNum: number | string): Satellite | null {
    // sccNum2Id handles all input forms (numeric, alpha-5, extended) plus
    // leading-zero normalization, so no pre-processing needed here.
    const sat = this.getObject(this.sccNum2Id(sccNum));

    if (!sat?.isSatellite()) {
      errorManagerInstance.debug(`Object ${sccNum} is not a satellite!`);

      return null;
    }

    return sat as Satellite;
  }

  a52Sat(sccNum: string): Satellite | null {
    const sat = this.getObject(this.sccNum2Id(sccNum));

    if (!sat?.isSatellite()) {
      errorManagerInstance.debug(`Object ${sccNum} is not a satellite!`);

      return null;
    }

    return sat as Satellite;
  }

  /**
   * Resolves a JSC Vimpel identifier to its objectCache id.
   *
   * Vimpel objects carry no NORAD sccNum (the catalog loader stores them with
   * `sccNum: ''`); their stable identity is the Vimpel altId parsed from
   * vimpel.json. There is no index for altIds, so this is a linear scan.
   * @param vimpelId The Vimpel altId (e.g. "12345").
   * @returns The corresponding objectCache id, or null when not found.
   */
  vimpelId2Id(vimpelId: string): number | null {
    if (!vimpelId) {
      return null;
    }

    for (let i = 0; i < this.objectCache.length; i++) {
      const obj = this.objectCache[i];

      if (obj instanceof Satellite && obj.source === CatalogSource.VIMPEL && obj.altId === vimpelId) {
        return i;
      }
    }

    return null;
  }

  /**
   * Finds the next unused analyst satellite slot - an inactive PAYLOAD reserved in
   * the {@link CatalogManager.ANALYST_START_ID} block. Tools that host a generated
   * nominal satellite (e.g. New Launch) claim one of these slots. Returns null when
   * every analyst slot in the search window is already active.
   *
   * @param startOffset - First offset past ANALYST_START_ID to consider.
   * @param endOffset - Exclusive upper offset bound for the search.
   */
  getNextAvailableAnalystSat(startOffset = 500, endOffset = 2500): Satellite | null {
    for (let offset = startOffset; offset < endOffset; offset++) {
      const sat = this.sccNum2Sat(CatalogManager.ANALYST_START_ID + offset);

      if (sat && !sat.active) {
        return sat;
      }
    }

    return null;
  }

  /**
   * @deprecated - Stars are not currently working
   *
   * Converts a star name to its corresponding ID within a given range.
   * @param starName - The name of the star.
   * @param starIndex1 - The starting index of the range.
   * @param starIndex2 - The ending index of the range.
   * @returns The ID of the star if found within the range, otherwise null.
   */
  starName2Id(starName: string, starIndex1: number, starIndex2: number): number | null {
    const i = this.objectCache.slice(starIndex1, starIndex2).findIndex((object) => object?.type === SpaceObjectType.STAR && object?.name === starName);

    return i === -1 ? null : i + starIndex1;
  }

  /**
   * Retrieves a satellite object from the catalog.
   *
   * Optional GetSatType parameter can be used speed up the function by retrieving only the required data.
   *
   */
  getObject(i: string | number | null | undefined, type: GetSatType = GetSatType.DEFAULT): BaseObject | null {
    // Convert string IDs to numbers for array access
    const index = typeof i === 'string' ? Number.parseInt(i, 10) : i;

    if (index === null || typeof index === 'undefined' || (index ?? -1) <= -1) {
      // errorManagerInstance.debug('getSat: i is null'); - This happens a lot but is useful for debugging

      return null;
    }

    if (index === -1 || !this.objectCache?.[index]) {
      if (!isThisNode() && index >= 0 && !this.objectCache[index]) {
        errorManagerInstance.debug(`Satellite ${index} not found`);
      }

      return null;
    }

    if (type === GetSatType.EXTRA_ONLY) {
      return this.objectCache[index];
    }

    if (type === GetSatType.POSITION_ONLY) {
      (this.objectCache[index] as Satellite).position = ServiceLocator.getDotsManager().getCurrentPosition(index);

      return this.objectCache[index];
    }

    if (type !== GetSatType.SKIP_POS_VEL) {
      ServiceLocator.getDotsManager().updatePosVel(this.objectCache[index], index);
    }

    return this.objectCache[index];
  }

  getSat(satId: string | number, type: GetSatType = GetSatType.DEFAULT): Satellite | null {
    const sat = this.getObject(satId, type);

    if (!sat?.isSatellite()) {
      return null;
    }

    return sat as Satellite;
  }

  getSats(): Satellite[] {
    // sats are the first numSats objects in the objectCache.
    // Use instanceof Satellite (not isSatellite()) so OemSatellite — which returns true
    // from isSatellite() but lacks tle1/tle2/apogee/perigee — does not leak through.
    return this.objectCache.slice(0, this.numSatellites).filter((sat): sat is Satellite => sat instanceof Satellite);
  }

  getMissile(missileId: string | number): MissileObject | null {
    const missile = this.getObject(missileId);

    if (!missile?.isMissile()) {
      return null;
    }

    return missile as MissileObject;
  }

  getSensorFromSensorName(sensorName: string): number {
    const index = this.objectCache.findIndex((object: BaseObject) => object.isSensor() && object.name === sensorName);

    return index;
  }

  id2satnum(satIdArray: number[]) {
    return satIdArray.map((id) => ((<Satellite>this.getObject(id))?.sccNum || -1).toString()).filter((satnum) => satnum !== '-1');
  }

  init(satCruncherOveride?: Worker): void {
    if (!satCruncherOveride) {
      const threadManager = new SatCruncherThreadManager(KeepTrack.getInstance().threads);

      SplashScreen.loadStr(SplashScreen.msg.elsets);
      threadManager.init();

      if (threadManager.worker === null) {
        throw new Error('satCruncher worker is null');
      }

      this.satCruncherThread = threadManager;
    } else {
      // Test/override path: wrap the raw Worker in a minimal thread manager
      const threadManager = new SatCruncherThreadManager(KeepTrack.getInstance().threads);

      threadManager.init(satCruncherOveride);
      this.satCruncherThread = threadManager;
    }
  }

  getActiveSats(): Satellite[] {
    return this.objectCache.filter((obj) => obj.isSatellite() && obj.active) as Satellite[];
  }

  initObjects() {
    // Create a buffer of missile objects
    for (let i = 0; i < settingsManager.maxMissiles; i++) {
      this.missileSet.push(
        new MissileObject({
          active: false,
          type: SpaceObjectType.BALLISTIC_MISSILE,
          name: `Missile ${i}`,
          latList: [],
          lonList: [],
          altList: [],
          timeList: [],
        } as unknown as MissileParams)
      );
    }

    // Create a buffer of analyst satellite objects
    for (let i = 0; i < settingsManager.maxAnalystSats; i++) {
      const sccNum = Tle.convert6DigitToA5((CatalogManager.ANALYST_START_ID + i).toString());

      this.analSatSet.push(
        new Satellite({
          active: false,
          name: `Analyst Sat ${i}`,
          country: 'ANALSAT',
          launchVehicle: 'Analyst Satellite',
          launchSite: 'ANALSAT',
          sccNum,
          tle1: `${CatalogManager.TEMPLATE_TLE1_BEGINNING}${sccNum}${CatalogManager.TEMPLATE_TLE1_ENDING}` as TleLine1,
          tle2: `${CatalogManager.TEMPLATE_TLE2_BEGINNING}${sccNum}${CatalogManager.TEMPLATE_TLE2_ENDING}` as TleLine2,
          intlDes: CatalogManager.TEMPLATE_INTLDES,
          type: SpaceObjectType.PAYLOAD,
          id: i,
        })
      );
    }

    // Create Stars
    if (!settingsManager.lowPerf && !settingsManager.isDisableStars) {
      stars.forEach((star) => {
        this.staticSet.push({
          name: star.name,
          isStatic: () => true,
          type: SpaceObjectType.STAR,
          dec: star.dec as Radians,
          ra: star.ra as Radians,
          vmag: star.vmag,
        } as Star);
      });
      this.isStarManagerLoaded = true;
    } else {
      this.isStarManagerLoaded = false;
    }

    // Create Sensors
    if (!settingsManager.isDisableSensors) {
      let i = 0;

      for (const sensor in sensors) {
        if (Object.hasOwn(sensors, sensor)) {
          sensors[sensor].sensorId = i;
          this.staticSet.push(sensors[sensor]);
          i++;
        }
      }
    }

    // Create Launch Sites
    if (!settingsManager.isDisableLaunchSites) {
      for (const launchSiteName in launchSiteObjects) {
        if (launchSiteObjects[launchSiteName] instanceof LaunchSite) {
          this.staticSet.push(launchSiteObjects[launchSiteName]);
        }
      }
      this.launchSites = launchSites;
      this.isLaunchSiteManagerLoaded = true;
    } else {
      this.isLaunchSiteManagerLoaded = false;
    }

    // Try Loading the Control Site Module
    if (!settingsManager.isDisableControlSites) {
      controlSites
        // Remove any control sites that are closed
        .filter((controlSite) => controlSite.TStop === '')
        // Until all the control sites enums are implemented ignore the odd ones
        .filter((controlSite) => controlSite.type < SpaceObjectType.MAX_SPACE_OBJECT_TYPE)
        .filter(StringExtractor.controlSiteTypeFilter)
        // Add the static properties to the control site objects
        .map((controlSite) => ({ ...{ static: true }, ...controlSite }))
        // Add the control site objects to the static set
        .forEach((controlSite) => {
          this.staticSet.push(controlSite);
        });
    }

    if (typeof settingsManager.maxFieldOfViewMarkers !== 'undefined') {
      for (let i = 0; i < settingsManager.maxFieldOfViewMarkers; i++) {
        const fieldOfViewMarker = {
          static: true,
          marker: true,
          id: i,
        };

        this.fieldOfViewSet.push(fieldOfViewMarker);
      }
    } else {
      errorManagerInstance.debug('settingsManager.maxFieldOfViewMarkers missing or broken!');
    }

    // Initialize the satLinkMananger and then attach it to the object manager
    try {
      const satLinkManager = new SatLinkManager();

      satLinkManager.init(controlSites);
      this.satLinkManager = satLinkManager;
    } catch {
      errorManagerInstance.debug('satLinkManager Failed to Initialize!');
    }
  }

  /**
   * @param sccNum The canonical sccNum for this satellite. Strongly recommended
   *   for extended (7+ digit) IDs: the TLE column 3-7 substring fallback only
   *   reads the trailing 5 chars and will silently lose precision otherwise.
   */
  addAnalystSat(tle1: string, tle2: string, id: number, sccNum?: string): Satellite | null {
    if (tle1.length !== 69) {
      throw new Error(`Invalid TLE1: length is not 69 - ${tle1}`);
    }
    if (tle2.length !== 69) {
      throw new Error(`Invalid TLE1: length is not 69 - ${tle2}`);
    }

    let satrec: SatelliteRecord;

    try {
      satrec = Sgp4.createSatrec(tle1, tle2);
    } catch (e) {
      errorManagerInstance.error(e, 'catalog-manager.ts', 'Error creating satellite record!');

      return null;
    }

    if (SatMath.altitudeCheck(satrec, ServiceLocator.getTimeManager().simulationTimeObj) > 1) {
      this.objectCache[id] = new Satellite({
        active: true,
        name: `Analyst Sat ${id}`,
        country: 'ANALSAT',
        launchVehicle: 'Analyst Satellite',
        launchSite: 'ANALSAT',
        // Fallback reads the TLE col 3-7 satnum (max 5 chars); extended IDs
        // can only be preserved by passing the explicit sccNum parameter.
        sccNum: sccNum ?? tle1.substring(2, 7).trim().padStart(5, '0'),
        tle1: tle1 as TleLine1,
        tle2: tle2 as TleLine2,
        intlDes: tle1.substring(9, 17),
        type: SpaceObjectType.PAYLOAD,
        id,
      });

      this.satCruncherThread.sendSatEdit(id, tle1, tle2, true);
      ServiceLocator.getOrbitManager().changeOrbitBufferData(id, tle1, tle2);
      const sat = this.objectCache[id] as Satellite;

      if (!sat.isSatellite()) {
        throw new Error(`Object ${id} is not a satellite!`);
      }

      this.seedDotPosition(id);

      return sat;
    }
    errorManagerInstance.debug(tle1);
    errorManagerInstance.debug(tle2);
    errorManagerInstance.warn('New Analyst Satellite is Invalid!');

    return null;
  }

  /**
   * Seeds the render buffers for a satellite that was just created or edited
   * on the main thread (analyst sats, breakups, created/edited satellites).
   *
   * The position cruncher processes sendSatEdit asynchronously, so until its
   * next cycle the slot still holds the placeholder 0,0,0 position, which the
   * search results and renderer read as "Decayed". The color worker likewise
   * holds a catalog snapshot with the slot inactive, leaving the dot
   * transparent. Consumers search for and select the new satellite immediately
   * after creation, so both buffers are nudged synchronously here.
   */
  seedDotPosition(id: number): void {
    // Best effort: render managers may not be registered yet (startup, tests)
    try {
      const sat = this.objectCache[id] as Satellite | undefined;

      if (!sat?.isSatellite?.()) {
        return;
      }

      const { position, velocity } = SatMath.getEci(sat, ServiceLocator.getTimeManager().simulationTimeObj);
      const dotsManagerInstance = ServiceLocator.getDotsManager();

      if (dotsManagerInstance?.positionData && (position.x !== 0 || position.y !== 0 || position.z !== 0)) {
        dotsManagerInstance.positionData[id * 3] = position.x;
        dotsManagerInstance.positionData[id * 3 + 1] = position.y;
        dotsManagerInstance.positionData[id * 3 + 2] = position.z;

        if (dotsManagerInstance.velocityData) {
          dotsManagerInstance.velocityData[id * 3] = velocity.x;
          dotsManagerInstance.velocityData[id * 3 + 1] = velocity.y;
          dotsManagerInstance.velocityData[id * 3 + 2] = velocity.z;
        }
      }

      ServiceLocator.getColorSchemeManager()?.notifyObjectsChanged?.();
    } catch (e) {
      errorManagerInstance.debug(`seedDotPosition skipped: ${(e as Error).message}`);
    }
  }

  buildOrbitDensityMatrix_() {
    const activeSats = this.getSats().filter((sat) => sat.active);

    this.orbitDensity = this.calculateOrbitalDensity_(activeSats, 25);
    this.buildOrbitPlaneDensityMatrix_(activeSats);
  }

  private calculateEffectiveAltitude_(satellite: Satellite): number {
    // Using the mean altitude approach
    return (satellite.apogee + satellite.perigee) / 2;
  }

  private buildOrbitPlaneDensityMatrix_(satellites: Satellite[]) {
    // Build the orbit density matrix
    for (let i = 0; i < 180; i += 2) {
      this.orbitalPlaneDensity[i] = [];
      for (let a = 75; a < 40000; a += 25) {
        this.orbitalPlaneDensity[i][a] = 0;
      }
    }

    for (let i = 0; i < satellites.length; i++) {
      // Static objects lack these values and including them increase the JS heap a lot
      if (!satellites[i].active) {
        continue;
      }
      const sat = satellites[i] as Satellite;

      if (satellites[i].active) {
        const inc = Math.floor(sat.inclination / 2) * 2;
        const alt = Math.floor(this.calculateEffectiveAltitude_(sat) / 25) * 25;

        this.orbitalPlaneDensity[inc][alt] += 1;
      }

      satellites[i].velocity = { x: 0, y: 0, z: 0 } as TemeVec3<KilometersPerSecond>;
    }

    this.orbitalPlaneDensityMax = 0;
    for (let i = 0; i < 180; i += 2) {
      for (let alt = 75; alt < 40000; alt += 25) {
        if (this.orbitalPlaneDensity[i][alt] > this.orbitalPlaneDensityMax) {
          this.orbitalPlaneDensityMax = this.orbitalPlaneDensity[i][alt];
        }
      }
    }
  }

  private calculateOrbitalDensity_(satellites: Satellite[], binSize: number = 25): DensityBin[] {
    const altitudes = satellites.map((satellite) => ({
      id: satellite.id,
      sccNum: satellite.sccNum,
      altitude: this.calculateEffectiveAltitude_(satellite),
    }));

    // Sort satellites by altitude
    const sortedSatellites = [...altitudes].sort((a, b) => a.altitude - b.altitude);

    /*
     * Find min and max altitudes
     */
    // const minAltitude = Math.floor(sortedSatellites[0].altitude / binSize) * binSize;
    const minAltitude = 75;
    //  const maxAltitude = Math.ceil(sortedSatellites[sortedSatellites.length - 1].altitude / binSize) * binSize;
    const maxAltitude = 2000;

    // Create altitude bins
    const bins: DensityBin[] = [];

    for (let alt = minAltitude; alt < maxAltitude; alt += binSize) {
      bins.push({
        minAltitude: alt,
        maxAltitude: alt + binSize,
        count: 0,
        density: 0,
      });
    }

    // Count satellites in each bin
    for (const satellite of sortedSatellites) {
      const binIndex = Math.floor((satellite.altitude - minAltitude) / binSize);

      if (binIndex >= 0 && binIndex < bins.length) {
        bins[binIndex].count++;
      }
    }

    // Calculate density for each bin
    for (const bin of bins) {
      // Calculate the volume of the spherical shell (in km³)
      const innerRadius = 6371 + bin.minAltitude; // Earth radius (6371 km) + min altitude
      const outerRadius = 6371 + bin.maxAltitude; // Earth radius (6371 km) + max altitude
      const volume = (4 / 3) * Math.PI * (outerRadius ** 3 - innerRadius ** 3);

      // Calculate spatial density (objects per km³)
      bin.density = bin.count / volume;
    }

    return bins;
  }
}
